import { useEffect } from "react";
import { Link } from "react-router-dom";
import useReveal from "../components/useReveal";
import CinematicHero from "../components/CinematicHero";

const sutraLine =
  "Sutra means thread. + Node means connection. + Strategy, design, growth, and systems moving as one. + ";

const stats = [
  { num: "7+", sub: "Years of learning & building" },
  { num: "300+", sub: "Projects delivered" },
  { num: "3x", sub: "Avg. traffic lift for clients" },
  { num: "8/10", sub: "Clients who continue with us" },
];

const fitCards = [
  {
    n: "01",
    title: "Wellness & Retreats",
    body:
      "Yoga studios, ayurveda clinics, holistic centres, and retreat spaces that deserve a digital presence as warm and inviting as the experience itself.",
    bullets: [
      "Booking-first websites",
      "Organic search that grows steadily",
      "Content that earns trust early",
    ],
  },
  {
    n: "02",
    title: "Hospitality & Resorts",
    body:
      "Hotels, eco-resorts, homestays, and destination properties where the guest journey starts online — long before check-in.",
    bullets: [
      "Sites that turn browsers into guests",
      "SEO that holds its own against large OTAs",
      "Ad campaigns that reach real travellers",
    ],
    gold: true,
  },
  {
    n: "03",
    title: "E-commerce & Lifestyle",
    body:
      "Product brands, Shopify stores, and local businesses going online with a stronger blend of storytelling, operations, and performance.",
    bullets: [
      "Conversion-aware storefronts",
      "AI tools that save production time",
      "Paid and organic channels in sync",
    ],
  },
];

export default function Home() {
  useReveal();

  useEffect(() => {
    document.title = "Sutranode — Digital Growth Studio";
  }, []);

  return (
    <>
      {/* HERO — cinematic video background */}
      <CinematicHero />

      {/* PHILOSOPHY STRIP — repeats the sutra/node line */}
      <div className="philosophy-strip">
        <div className="phi-track">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i}>
              {sutraLine.split("+").map((part, j) =>
                part.trim() ? (
                  <span key={j} style={{ display: "inline-flex", alignItems: "center", gap: 16 }}>
                    {part.trim()}
                    <span className="phi-sep">◆</span>
                  </span>
                ) : null
              )}
            </span>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((s, i) => (
            <div key={i} className="stat-block" data-reveal>
              <div className="stat-num">{s.num}</div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="section">
        <div className="container">
          <div className="section-heading" data-reveal>
            <div className="s-eyebrow">Who this is for</div>
            <h2 className="s-title">Built for brands that <em>care about quality.</em></h2>
            <p className="fit-intro">
              Every business is different. These are some of the spaces where we've done meaningful
              work — but if you're building something you believe in, we'd love to hear about it
              regardless of category.
            </p>
          </div>

          <div className="fit-grid">
            {fitCards.map((card, i) => (
              <div key={i} className={`fit-card ${card.gold ? "fit-card-gold" : ""}`} data-reveal>
                <div className="fit-number">{card.n}</div>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
                <ul>
                  {card.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className="fit-cta" data-reveal>
            <span>Working on something interesting?</span>
            <Link to="/contact" className="btn-gold">Let's talk →</Link>
          </div>
        </div>
      </section>

      {/* MEGA CTA */}
      <section className="section">
        <div className="container">
          <div className="mega-cta" data-reveal>
            <h2>
              Ready to build something
              <br />
              <em>that outlasts the feed?</em>
            </h2>
            <p>
              One honest conversation is all it takes to find out whether we're
              the right thread for what you're building.
            </p>
            <div className="mega-cta-actions">
              <Link to="/contact" className="btn-gold">Start a project →</Link>
              <Link to="/services" className="btn-ghost">Explore services</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
